import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Card, CardContent, TextField, Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ofpService } from '../services/ofpService';

function OFPStatus() {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [entityType, setEntityType] = useState('organization');
  const [entityId, setEntityId] = useState('');

  useEffect(() => { checkConnection(); }, []);

  const checkConnection = async () => {
    try {
      setLoading(true);
      const status = await ofpService.getStatus();
      setConnectionStatus(status);
    } catch (err) {
      setConnectionStatus({ connected: false, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!entityId) return;
    try {
      const result = await ofpService.validateEntity(entityType, entityId);
      setValidationResult(result);
    } catch (err) {
      setValidationResult({ valid: false, error: err.response?.data?.error?.message || err.message });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Open Footprint Integration</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        OFP database connection status and entity validation
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Connection Status</Typography>
              {connectionStatus?.connected ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Connected to Open Footprint database (read-only)
                </Alert>
              ) : (
                <Alert severity="warning" icon={<ErrorIcon />}>
                  OFP database not available: {connectionStatus?.error || 'Unknown error'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>OFP Domains</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="master_data.organization" size="small" variant="outlined" />
                <Chip label="master_data.facility" size="small" variant="outlined" />
                <Chip label="master_data.product" size="small" variant="outlined" />
                <Chip label="master_data.equipment" size="small" variant="outlined" />
                <Chip label="master_data.emission_activity" size="small" variant="outlined" />
                <Chip label="master_data.emission_factor" size="small" variant="outlined" />
                <Chip label="transactional_data.emission_statement" size="small" variant="outlined" />
                <Chip label="reference_data.unit_of_measure" size="small" variant="outlined" />
                <Chip label="transactional_data.product_carbon_footprint" size="small" variant="outlined" color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Entity Validation</Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={3}>
            <TextField fullWidth select label="Entity Type" value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              SelectProps={{ native: true }}>
              <option value="organization">Organization</option>
              <option value="facility">Facility</option>
              <option value="equipment">Equipment</option>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Entity ID" value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="Enter OFP entity primary key" />
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" fullWidth onClick={handleValidate} disabled={!entityId}>
              Validate
            </Button>
          </Grid>
        </Grid>

        {validationResult && (
          <Box sx={{ mt: 2 }}>
            {validationResult.valid ? (
              <Alert severity="success">
                Entity found: {validationResult.entity?.name || entityId}
              </Alert>
            ) : (
              <Alert severity="error">
                Validation failed: {validationResult.error}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default OFPStatus;
